import React, { useState } from "react";
import {
    Search,
    FileText,
    CheckCircle2,
    Package,
    Truck,
    Clock,
    ChevronRight,
    MapPin,
    IndianRupee,
    Star,
    Cog,
    MessageSquare,
    TrendingUp,
    Activity,
    Bell,
    Wrench,
    ArrowUpRight,
    ArrowDownRight,
    Circle,
} from "lucide-react";

// ---- Sample data (swap for real API data) ----------------------------------
const vendor = {
    name: "Veltrix Electronics Pvt Ltd",
    location: "Vizg, AP",
    rating: 4.8,
    reviews: 142,
    memberSince: "2023",
};

const machines = [
    { id: "M-01", name: "CNC Milling Machine — HAAS VF2", status: "running", utilization: 92 },
    { id: "M-02", name: "SMT Pick & Place Line", status: "running", utilization: 78 },
    { id: "M-03", name: "Reflow Oven — 8 Zone", status: "idle", utilization: 34 },
    { id: "M-04", name: "Laser Cutting Unit", status: "maintenance", utilization: 0 },
    { id: "M-05", name: "Injection Moulding Press", status: "running", utilization: 65 },
    { id: "M-06", name: "3-Axis Robotic Welder", status: "idle", utilization: 12 },
];

const recentOrders = [
    {
        id: "APIC-ORD-00081",
        item: "Smart IoT Water Sensor",
        buyer: "Apex Robotics Pvt Ltd",
        amount: 178000,
        status: "pending_acceptance",
        date: "2026-07-20",
    },
    {
        id: "APIC-ORD-00080",
        item: "Precision Steel Brackets",
        buyer: "Sharma Auto Components",
        amount: 62000,
        status: "in_production",
        date: "2026-07-18",
    },
    {
        id: "APIC-ORD-00079",
        item: "ABS Enclosure Set (Grey)",
        buyer: "PrintForm Buyers Co.",
        amount: 41500,
        status: "shipped",
        date: "2026-07-15",
    },
    {
        id: "APIC-ORD-00076",
        item: "Custom Wiring Harness",
        buyer: "Anand Mobility Ltd",
        amount: 95000,
        status: "delivered",
        date: "2026-07-05",
    },
];

const chats = [
    {
        id: "c1",
        name: "Apex Robotics Pvt Ltd",
        lastMessage: "Can you confirm the delivery date for the sensor batch?",
        time: "10:42 AM",
        unread: 2,
    },
    {
        id: "c2",
        name: "Sharma Auto Components",
        lastMessage: "Attaching the revised drawing for the brackets.",
        time: "9:15 AM",
        unread: 0,
    },
    {
        id: "c3",
        name: "Anand Mobility Ltd",
        lastMessage: "Payment has been released. Thank you!",
        time: "Yesterday",
        unread: 0,
    },
    {
        id: "c4",
        name: "Metro Industrial Buyers",
        lastMessage: "What's your MOQ for aluminium heat sinks?",
        time: "Yesterday",
        unread: 1,
    },
];

// ---- Status configuration ---------------------------------------------------
const STATUS_CONFIG = {
    pending_acceptance: { label: "Awaiting Buyer", bg: "#FFF7E6", text: "#B45309", dot: "#4f46e5" },
    in_production: { label: "In Production", bg: "#E9F7FE", text: "#0369A1", dot: "#0EA5E9" },
    shipped: { label: "Shipped", bg: "#F3EEFE", text: "#6D28D9", dot: "#8B5CF6" },
    delivered: { label: "Delivered", bg: "#EAFBF1", text: "#15803D", dot: "#22C55E" },
};

const MACHINE_STATUS = {
    running: { label: "Running", color: "#22C55E" },
    idle: { label: "Idle", color: "#94A3B8" },
    maintenance: { label: "Maintenance", color: "#EF4444" },
};

const currency = (n) => "\u20B9" + n.toLocaleString("en-IN");
const formatDate = (d) =>
    new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });

// ---- Custom scrollbar --------------------------------------------------------
function ScrollbarStyles() {
    return (
        <style>{`
      .custom-scrollbar::-webkit-scrollbar { width: 8px; height: 8px; }
      .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
      .custom-scrollbar::-webkit-scrollbar-thumb {
        background-color: #CBD5E1;
        border-radius: 9999px;
        border: 2px solid transparent;
        background-clip: padding-box;
      }
      .custom-scrollbar::-webkit-scrollbar-thumb:hover { background-color: #94A3B8; }
      .custom-scrollbar { scrollbar-width: thin; scrollbar-color: #CBD5E1 transparent; }
    `}</style>
    );
}

// ---- Stat card ----------------------------------------------------------------
function StatCard({ label, value, sub, icon: Icon, accent, trend }) {
    return (
        <div className="rounded-2xl border border-slate-200 bg-white px-6 py-5 flex flex-col gap-3">
            <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold tracking-wider text-slate-400 uppercase">
                    {label}
                </span>
                <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: accent + "15" }}
                >
                    <Icon size={16} style={{ color: accent }} />
                </div>
            </div>
            <div className="flex items-end justify-between">
                <span className="text-2xl font-bold text-slate-900">{value}</span>
                {trend && (
                    <span
                        className={`flex items-center gap-0.5 text-xs font-semibold ${trend.dir === "up" ? "text-emerald-600" : "text-rose-500"
                            }`}
                    >
                        {trend.dir === "up" ? (
                            <ArrowUpRight size={13} />
                        ) : (
                            <ArrowDownRight size={13} />
                        )}
                        {trend.value}
                    </span>
                )}
            </div>
            {sub && <span className="text-xs text-slate-400">{sub}</span>}
        </div>
    );
}

// ---- Main page -----------------------------------------------------------------
export default function VendorDashboard() {
    const [query, setQuery] = useState("");

    const stats = {
        totalMachines: machines.length,
        runningMachines: machines.filter((m) => m.status === "running").length,
        activeOrders: recentOrders.filter((o) =>
            ["pending_acceptance", "in_production", "shipped"].includes(o.status)
        ).length,
        totalOrders: recentOrders.length + 63, // + historical
        revenueThisMonth: 486200,
        unreadChats: chats.reduce((s, c) => s + c.unread, 0),
    };

    const filteredOrders = recentOrders.filter((o) => {
        const q = query.toLowerCase();
        return (
            o.item.toLowerCase().includes(q) ||
            o.id.toLowerCase().includes(q) ||
            o.buyer.toLowerCase().includes(q)
        );
    });

    return (
        <div className="h-screen overflow-y-auto custom-scrollbar bg-slate-50 py-8 px-6 font-sans">
            <ScrollbarStyles />
            <div className="max-w-6xl mx-auto space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                        <h1 className="text-xl font-bold text-slate-900">
                            Welcome back, {vendor.name.split(" ")[0]}
                        </h1>
                        <div className="flex items-center gap-3 text-sm text-slate-500 mt-1">
                            <span className="flex items-center gap-1">
                                <MapPin size={13} /> {vendor.location}
                            </span>
                            <span className="mx-0.5">&middot;</span>
                            <span className="flex items-center gap-1">
                                <Star size={13} className="fill-amber-400 text-amber-400" />
                                {vendor.rating} ({vendor.reviews} reviews)
                            </span>
                            <span className="mx-0.5">&middot;</span>
                            <span>Member since {vendor.memberSince}</span>
                        </div>
                    </div>
                    <button className="relative inline-flex items-center gap-1.5 border border-slate-200 bg-white text-slate-600 text-sm font-medium px-4 py-2.5 rounded-xl hover:bg-slate-50">
                        <Bell size={15} /> Notifications
                        <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center">
                            3
                        </span>
                    </button>
                </div>

                {/* Stat cards */}
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
                    <StatCard
                        label="Total Machines"
                        value={stats.totalMachines}
                        sub={`${stats.runningMachines} running now`}
                        icon={Cog}
                        accent="#6366F1"
                    />
                    <StatCard
                        label="Active Orders"
                        value={stats.activeOrders}
                        icon={Package}
                        accent="#0EA5E9"
                    />
                    <StatCard
                        label="Total Orders"
                        value={stats.totalOrders}
                        icon={Truck}
                        accent="#8B5CF6"
                        trend={{ dir: "up", value: "12%" }}
                    />
                    <StatCard
                        label="Revenue (MTD)"
                        value={currency(stats.revenueThisMonth)}
                        icon={IndianRupee}
                        accent="#22C55E"
                        trend={{ dir: "up", value: "8%" }}
                    />
                    <StatCard
                        label="Chats"
                        value={chats.length}
                        sub={`${stats.unreadChats} unread`}
                        icon={MessageSquare}
                        accent="#4f46e5"
                    />
                    <StatCard
                        label="Vendor Rating"
                        value={vendor.rating}
                        sub={`${vendor.reviews} reviews`}
                        icon={Star}
                        accent="#EC4899"
                    />
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                    {/* Left: Orders + Machines */}
                    <div className="xl:col-span-2 space-y-6">
                        {/* Recent Orders */}
                        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
                            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
                                <h2 className="font-bold text-slate-900 text-[15px]">Recent Orders</h2>
                                <div className="relative">
                                    <Search
                                        size={14}
                                        className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                                    />
                                    <input
                                        value={query}
                                        onChange={(e) => setQuery(e.target.value)}
                                        placeholder="Search orders..."
                                        className="rounded-full border border-slate-200 bg-slate-50 pl-8 pr-3 py-1.5 text-xs text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 w-44"
                                    />
                                </div>
                            </div>

                            <div className="overflow-auto max-h-[360px] custom-scrollbar">
                                <table className="w-full text-sm">
                                    <thead className="sticky top-0 z-10 bg-white">
                                        <tr className="text-left text-[11px] font-semibold tracking-wider text-slate-400 uppercase border-b border-slate-100">
                                            <th className="px-6 py-3 font-semibold">Order</th>
                                            <th className="px-4 py-3 font-semibold">Buyer</th>
                                            <th className="px-4 py-3 font-semibold">Amount</th>
                                            <th className="px-4 py-3 font-semibold">Status</th>
                                            <th className="px-6 py-3 font-semibold text-right">Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredOrders.map((o) => {
                                            const cfg = STATUS_CONFIG[o.status];
                                            return (
                                                <tr
                                                    key={o.id}
                                                    className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60 transition-colors"
                                                >
                                                    <td className="px-6 py-4">
                                                        <div className="font-semibold text-slate-900">{o.item}</div>
                                                        <div className="text-xs text-slate-400 mt-0.5">{o.id}</div>
                                                    </td>
                                                    <td className="px-4 py-4 text-slate-600">{o.buyer}</td>
                                                    <td className="px-4 py-4 font-medium text-slate-800">
                                                        {currency(o.amount)}
                                                    </td>
                                                    <td className="px-4 py-4">
                                                        <span
                                                            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium"
                                                            style={{ backgroundColor: cfg.bg, color: cfg.text }}
                                                        >
                                                            <span
                                                                className="w-1.5 h-1.5 rounded-full"
                                                                style={{ backgroundColor: cfg.dot }}
                                                            />
                                                            {cfg.label}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 text-right text-slate-500">
                                                        {formatDate(o.date)}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        {filteredOrders.length === 0 && (
                                            <tr>
                                                <td colSpan={5} className="px-6 py-10 text-center text-slate-400 text-sm">
                                                    No orders match your search.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Machines */}
                        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
                            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                                <h2 className="font-bold text-slate-900 text-[15px]">Machines</h2>
                                <span className="text-xs text-slate-400">
                                    {stats.runningMachines} of {stats.totalMachines} running
                                </span>
                            </div>
                            <div className="divide-y divide-slate-50 max-h-[340px] overflow-y-auto custom-scrollbar">
                                {machines.map((m) => {
                                    const cfg = MACHINE_STATUS[m.status];
                                    return (
                                        <div
                                            key={m.id}
                                            className="px-6 py-4 flex items-center justify-between gap-4 hover:bg-slate-50/60 transition-colors"
                                        >
                                            <div className="flex items-center gap-3 min-w-0">
                                                <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0">
                                                    {m.status === "maintenance" ? (
                                                        <Wrench size={16} className="text-rose-500" />
                                                    ) : (
                                                        <Activity size={16} className="text-indigo-600" />
                                                    )}
                                                </div>
                                                <div className="min-w-0">
                                                    <div className="font-medium text-slate-800 text-sm truncate">
                                                        {m.name}
                                                    </div>
                                                    <div className="flex items-center gap-1.5 text-xs text-slate-400 mt-0.5">
                                                        <Circle size={7} fill={cfg.color} stroke="none" />
                                                        {cfg.label} &middot; {m.id}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="w-28 flex-shrink-0">
                                                <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                                                    <span>Utilization</span>
                                                    <span className="font-semibold text-slate-700">{m.utilization}%</span>
                                                </div>
                                                <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                                                    <div
                                                        className="h-full rounded-full"
                                                        style={{
                                                            width: `${m.utilization}%`,
                                                            backgroundColor:
                                                                m.utilization > 70
                                                                    ? "#22C55E"
                                                                    : m.utilization > 30
                                                                        ? "#4f46e5"
                                                                        : "#CBD5E1",
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Right: Chats */}
                    <div className="space-y-6">
                        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
                            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                                <h2 className="font-bold text-slate-900 text-[15px]">Chats</h2>
                                {stats.unreadChats > 0 && (
                                    <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold bg-indigo-100 text-indigo-700">
                                        {stats.unreadChats} new
                                    </span>
                                )}
                            </div>
                            <div className="divide-y divide-slate-50 max-h-[460px] overflow-y-auto custom-scrollbar">
                                {chats.map((c) => (
                                    <button
                                        key={c.id}
                                        className="w-full text-left px-6 py-4 flex items-start gap-3 hover:bg-slate-50/60 transition-colors"
                                    >
                                        <div className="w-9 h-9 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-semibold text-xs flex-shrink-0">
                                            {c.name
                                                .split(" ")
                                                .slice(0, 2)
                                                .map((w) => w[0])
                                                .join("")}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center justify-between gap-2">
                                                <span className="font-medium text-slate-800 text-sm truncate">
                                                    {c.name}
                                                </span>
                                                <span className="text-[11px] text-slate-400 flex-shrink-0">{c.time}</span>
                                            </div>
                                            <p className="text-xs text-slate-500 truncate mt-0.5">{c.lastMessage}</p>
                                        </div>
                                        {c.unread > 0 && (
                                            <span className="w-2 h-2 rounded-full bg-indigo-600 flex-shrink-0 mt-1.5" />
                                        )}
                                    </button>
                                ))}
                            </div>
                            <div className="px-6 py-3 border-t border-slate-100">
                                <button className="w-full text-center text-sm font-medium text-indigo-600 hover:text-indigo-800 flex items-center justify-center gap-1">
                                    Open all chats <ChevronRight size={14} />
                                </button>
                            </div>
                        </div>

                        {/* Quick summary card */}
                        <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
                            <h2 className="font-bold text-slate-900 text-[15px]">This Month</h2>
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-slate-500 flex items-center gap-1.5">
                                    <TrendingUp size={14} /> Revenue
                                </span>
                                <span className="font-semibold text-slate-900">
                                    {currency(stats.revenueThisMonth)}
                                </span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-slate-500 flex items-center gap-1.5">
                                    <FileText size={14} /> Quotations sent
                                </span>
                                <span className="font-semibold text-slate-900">27</span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-slate-500 flex items-center gap-1.5">
                                    <CheckCircle2 size={14} /> Win rate
                                </span>
                                <span className="font-semibold text-slate-900">41%</span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-slate-500 flex items-center gap-1.5">
                                    <Clock size={14} /> Avg. response time
                                </span>
                                <span className="font-semibold text-slate-900">2.4 hrs</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}




// VendorDashboard.jsx
// import React, { useState, useEffect, useCallback } from "react";
// import {
//     Search,
//     FileText,
//     CheckCircle2,
//     Package,
//     Truck,
//     Clock,
//     ChevronRight,
//     MapPin,
//     IndianRupee,
//     Star,
//     Cog,
//     MessageSquare,
//     TrendingUp,
//     Activity,
//     Bell,
//     Wrench,
//     ArrowUpRight,
//     ArrowDownRight,
//     Circle,
//     Loader2,
//     RefreshCw,
//     AlertCircle,
//     Menu,
//     X,
// } from "lucide-react";
// import { fetchAllVendorData } from "../service/Vendorservisefile/dashboardService.jsx";

// // ---- Status configuration ----
// const STATUS_CONFIG = {
//     pending_acceptance: { label: "Awaiting Buyer", bg: "#FFF7E6", text: "#B45309", dot: "#4f46e5" },
//     in_production: { label: "In Production", bg: "#E9F7FE", text: "#0369A1", dot: "#0EA5E9" },
//     shipped: { label: "Shipped", bg: "#F3EEFE", text: "#6D28D9", dot: "#8B5CF6" },
//     delivered: { label: "Delivered", bg: "#EAFBF1", text: "#15803D", dot: "#22C55E" },
//     pending: { label: "Pending", bg: "#FFF7E6", text: "#B45309", dot: "#4f46e5" },
//     accepted: { label: "Accepted", bg: "#E9F7FE", text: "#0369A1", dot: "#0EA5E9" },
//     rejected: { label: "Rejected", bg: "#FEE9E9", text: "#DC2626", dot: "#EF4444" },
//     cancelled: { label: "Cancelled", bg: "#FEE9E9", text: "#DC2626", dot: "#EF4444" },
// };

// const MACHINE_STATUS = {
//     running: { label: "Running", color: "#22C55E" },
//     idle: { label: "Idle", color: "#94A3B8" },
//     maintenance: { label: "Maintenance", color: "#EF4444" },
//     offline: { label: "Offline", color: "#94A3B8" },
//     error: { label: "Error", color: "#EF4444" },
// };

// // ---- Helper functions ----
// const currency = (n) => {
//     if (!n || isNaN(n)) return "₹0";
//     return "₹" + n.toLocaleString("en-IN");
// };

// const formatDate = (d) => {
//     if (!d) return 'N/A';
//     try {
//         const date = new Date(d);
//         if (isNaN(date.getTime())) return 'N/A';
//         return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
//     } catch {
//         return 'N/A';
//     }
// };

// // ---- Custom scrollbar styles ----
// function ScrollbarStyles() {
//     return (
//         <style>{`
//             .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
//             .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
//             .custom-scrollbar::-webkit-scrollbar-thumb {
//                 background-color: #CBD5E1;
//                 border-radius: 9999px;
//                 border: 2px solid transparent;
//                 background-clip: padding-box;
//             }
//             .custom-scrollbar::-webkit-scrollbar-thumb:hover { background-color: #94A3B8; }
//             .custom-scrollbar { scrollbar-width: thin; scrollbar-color: #CBD5E1 transparent; }
            
//             /* Responsive utilities */
//             @media (max-width: 640px) {
//                 .hide-mobile { display: none !important; }
//                 .show-mobile { display: flex !important; }
//             }
//             @media (min-width: 641px) {
//                 .show-mobile { display: none !important; }
//             }
//         `}</style>
//     );
// }

// // ---- Stat Card Component ----
// function StatCard({ label, value, sub, icon: Icon, accent, trend }) {
//     return (
//         <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4 sm:px-6 sm:py-5 flex flex-col gap-2 sm:gap-3 hover:shadow-md transition-shadow duration-200">
//             <div className="flex items-center justify-between">
//                 <span className="text-[10px] sm:text-[11px] font-semibold tracking-wider text-slate-400 uppercase">
//                     {label}
//                 </span>
//                 <div
//                     className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center flex-shrink-0"
//                     style={{ backgroundColor: accent + "15" }}
//                 >
//                     <Icon size={14} className="sm:w-4 sm:h-4" style={{ color: accent }} />
//                 </div>
//             </div>
//             <div className="flex items-end justify-between">
//                 <span className="text-lg sm:text-2xl font-bold text-slate-900 truncate">{value}</span>
//                 {trend && (
//                     <span
//                         className={`flex items-center gap-0.5 text-xs font-semibold ${
//                             trend.dir === "up" ? "text-emerald-600" : "text-rose-500"
//                         }`}
//                     >
//                         {trend.dir === "up" ? (
//                             <ArrowUpRight size={13} />
//                         ) : (
//                             <ArrowDownRight size={13} />
//                         )}
//                         {trend.value}
//                     </span>
//                 )}
//             </div>
//             {sub && <span className="text-[10px] sm:text-xs text-slate-400 truncate">{sub}</span>}
//         </div>
//     );
// }

// // ---- Loading Skeleton ----
// function LoadingSkeleton() {
//     return (
//         <div className="flex flex-col items-center justify-center h-64 sm:h-96 gap-4">
//             <Loader2 className="w-10 h-10 sm:w-12 sm:h-12 animate-spin text-indigo-600" />
//             <span className="text-slate-600 font-medium">Loading dashboard...</span>
//         </div>
//     );
// }

// // ---- Error Component ----
// function ErrorDisplay({ error, onRetry }) {
//     return (
//         <div className="max-w-6xl mx-auto">
//             <div className="bg-red-50 border border-red-200 rounded-2xl px-4 py-6 sm:px-6 sm:py-8 flex flex-col items-center text-center">
//                 <AlertCircle className="w-10 h-10 sm:w-12 sm:h-12 text-red-500 mb-3" />
//                 <h3 className="font-semibold text-red-700 text-base sm:text-lg">Error Loading Dashboard</h3>
//                 <p className="text-sm text-red-600 mt-1 max-w-md">{error}</p>
//                 <button 
//                     onClick={onRetry}
//                     className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
//                 >
//                     <RefreshCw size={16} /> Retry
//                 </button>
//             </div>
//         </div>
//     );
// }

// // ---- Main Dashboard Component ----
// export default function VendorDashboard() {
//     const [searchQuery, setSearchQuery] = useState("");
//     const [loading, setLoading] = useState(true);
//     const [error, setError] = useState(null);
//     const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
//     const vendorId = 1; // Change this to dynamic vendor ID

//     // State for all data
//     const [vendor, setVendor] = useState({
//         name: 'Vendor',
//         location: 'N/A',
//         rating: 0,
//         reviews: 0,
//         memberSince: '2024',
//         id: vendorId,
//     });

//     const [stats, setStats] = useState({
//         totalMachines: 0,
//         activeOrders: 0,
//         totalOrders: 0,
//         revenue: 0,
//         quotationsSent: 0,
//         avgResponseTime: 0,
//         chats: 0,
//     });

//     const [machines, setMachines] = useState([]);
//     const [orders, setOrders] = useState([]);
//     const [chats, setChats] = useState([]);
//     const [allVendorStats, setAllVendorStats] = useState({
//         totalMachines: 0,
//         activeOrders: 0,
//         totalOrders: 0,
//         revenue: 0,
//         quotationsSent: 0,
//         avgResponseTime: 0,
//         chats: 0,
//     });

//     // ---- Fetch all data using service ----
//     const fetchAllData = useCallback(async () => {
//         setLoading(true);
//         setError(null);
        
//         try {
//             const data = await fetchAllVendorData(vendorId);
            
//             // Update state with results
//             if (data.vendor) setVendor(data.vendor);
//             if (data.stats) setStats(data.stats);
//             if (data.machines) setMachines(data.machines);
//             if (data.orders) setOrders(data.orders);
//             if (data.chats) setChats(data.chats);
//             if (data.allVendorStats) setAllVendorStats(data.allVendorStats);
            
//         } catch (err) {
//             console.error('Error fetching dashboard data:', err);
//             setError('Failed to load dashboard data. Please try again.');
//         } finally {
//             setLoading(false);
//         }
//     }, [vendorId]);

//     // Load data on mount
//     useEffect(() => {
//         fetchAllData();
//     }, [fetchAllData]);

//     // Filter orders based on search query
//     const filteredOrders = orders.filter((o) => {
//         const q = searchQuery.toLowerCase();
//         return (
//             (o.item || '').toLowerCase().includes(q) ||
//             (o.id || '').toLowerCase().includes(q) ||
//             (o.buyer || '').toLowerCase().includes(q)
//         );
//     });

//     // Calculate derived stats
//     const runningMachines = machines.filter((m) => m.status === "running").length;
//     const activeOrders = orders.filter((o) =>
//         ["pending_acceptance", "in_production", "shipped", "pending", "accepted"].includes(o.status)
//     ).length;
//     const unreadChats = chats.reduce((s, c) => s + (c.unread || 0), 0);

//     if (loading) {
//         return (
//             <div className="h-screen overflow-y-auto custom-scrollbar bg-slate-50 py-4 px-3 sm:py-8 sm:px-6 font-sans">
//                 <ScrollbarStyles />
//                 <LoadingSkeleton />
//             </div>
//         );
//     }

//     if (error) {
//         return (
//             <div className="h-screen overflow-y-auto custom-scrollbar bg-slate-50 py-4 px-3 sm:py-8 sm:px-6 font-sans">
//                 <ScrollbarStyles />
//                 <ErrorDisplay error={error} onRetry={fetchAllData} />
//             </div>
//         );
//     }

//     return (
//         <div className="h-screen overflow-y-auto custom-scrollbar bg-slate-50 py-4 px-3 sm:py-8 sm:px-6 font-sans">
//             <ScrollbarStyles />
//             <div className="max-w-6xl mx-auto space-y-4 sm:space-y-6">
//                 {/* Header */}
//                 <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
//                     <div>
//                         <h1 className="text-lg sm:text-xl font-bold text-slate-900">
//                             Welcome back, {vendor.name.split(" ")[0]}
//                         </h1>
//                         <div className="flex flex-wrap items-center gap-1 sm:gap-3 text-xs sm:text-sm text-slate-500 mt-1">
//                             <span className="flex items-center gap-1">
//                                 <MapPin size={12} className="sm:w-[13px] sm:h-[13px]" />
//                                 {vendor.location}
//                             </span>
//                             <span className="mx-0.5">&middot;</span>
//                             <span className="flex items-center gap-1">
//                                 <Star size={12} className="sm:w-[13px] sm:h-[13px] fill-amber-400 text-amber-400" />
//                                 {vendor.rating} ({vendor.reviews} reviews)
//                             </span>
//                             <span className="mx-0.5 hide-mobile">&middot;</span>
//                             <span className="hide-mobile">Member since {vendor.memberSince}</span>
//                             <button
//                                 onClick={fetchAllData}
//                                 className="ml-1 sm:ml-2 p-1.5 hover:bg-slate-200 rounded-lg transition-colors"
//                                 title="Refresh data"
//                             >
//                                 <RefreshCw size={14} className="text-slate-400" />
//                             </button>
//                         </div>
//                     </div>
//                     <div className="flex items-center gap-2">
//                         <button className="relative inline-flex items-center gap-1.5 border border-slate-200 bg-white text-slate-600 text-xs sm:text-sm font-medium px-3 py-2 sm:px-4 sm:py-2.5 rounded-xl hover:bg-slate-50 transition-colors">
//                             <Bell size={14} className="sm:w-[15px] sm:h-[15px]" />
//                             <span className="hide-mobile">Notifications</span>
//                             <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center">
//                                 3
//                             </span>
//                         </button>
//                         <button 
//                             className="sm:hidden p-2 hover:bg-slate-200 rounded-lg transition-colors"
//                             onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
//                         >
//                             {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
//                         </button>
//                     </div>
//                 </div>

//                 {/* Stat cards - Responsive grid */}
//                 <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2 sm:gap-4">
//                     <StatCard
//                         label="Total Machines"
//                         value={stats.totalMachines || machines.length}
//                         sub={`${runningMachines} running now`}
//                         icon={Cog}
//                         accent="#6366F1"
//                     />
//                     <StatCard
//                         label="Active Orders"
//                         value={stats.activeOrders || activeOrders}
//                         icon={Package}
//                         accent="#0EA5E9"
//                     />
//                     <StatCard
//                         label="Total Orders"
//                         value={stats.totalOrders || orders.length}
//                         icon={Truck}
//                         accent="#8B5CF6"
//                         trend={{ dir: "up", value: "12%" }}
//                     />
//                     <StatCard
//                         label="Revenue (MTD)"
//                         value={currency(stats.revenue)}
//                         icon={IndianRupee}
//                         accent="#22C55E"
//                         trend={{ dir: "up", value: "8%" }}
//                     />
//                     <StatCard
//                         label="Chats"
//                         value={chats.length}
//                         sub={`${unreadChats} unread`}
//                         icon={MessageSquare}
//                         accent="#4f46e5"
//                     />
//                     <StatCard
//                         label="Quotations"
//                         value={stats.quotationsSent || 'N/A'}
//                         icon={Star}
//                         accent="#EC4899"
//                     />
//                 </div>

//                 <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-6">
//                     {/* Left: Orders + Machines */}
//                     <div className="xl:col-span-2 space-y-4 sm:space-y-6">
//                         {/* Recent Orders */}
//                         <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
//                             <div className="px-4 py-3 sm:px-6 sm:py-4 border-b border-slate-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-3">
//                                 <h2 className="font-bold text-slate-900 text-[14px] sm:text-[15px]">Recent Orders</h2>
//                                 <div className="relative w-full sm:w-auto">
//                                     <Search
//                                         size={14}
//                                         className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
//                                     />
//                                     <input
//                                         value={searchQuery}
//                                         onChange={(e) => setSearchQuery(e.target.value)}
//                                         placeholder="Search orders..."
//                                         className="w-full sm:w-44 rounded-full border border-slate-200 bg-slate-50 pl-8 pr-3 py-1.5 text-xs text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
//                                     />
//                                 </div>
//                             </div>

//                             <div className="overflow-x-auto max-h-[360px] custom-scrollbar">
//                                 <table className="w-full text-sm">
//                                     <thead className="sticky top-0 z-10 bg-white">
//                                         <tr className="text-left text-[10px] sm:text-[11px] font-semibold tracking-wider text-slate-400 uppercase border-b border-slate-100">
//                                             <th className="px-3 sm:px-6 py-2 sm:py-3 font-semibold">Order</th>
//                                             <th className="px-2 sm:px-4 py-2 sm:py-3 font-semibold hide-mobile">Buyer</th>
//                                             <th className="px-2 sm:px-4 py-2 sm:py-3 font-semibold">Amount</th>
//                                             <th className="px-2 sm:px-4 py-2 sm:py-3 font-semibold">Status</th>
//                                             <th className="px-3 sm:px-6 py-2 sm:py-3 font-semibold text-right hide-mobile">Date</th>
//                                         </tr>
//                                     </thead>
//                                     <tbody>
//                                         {filteredOrders.length > 0 ? (
//                                             filteredOrders.map((o) => {
//                                                 const cfg = STATUS_CONFIG[o.status] || STATUS_CONFIG.pending_acceptance;
//                                                 return (
//                                                     <tr
//                                                         key={o.id}
//                                                         className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60 transition-colors"
//                                                     >
//                                                         <td className="px-3 sm:px-6 py-3 sm:py-4">
//                                                             <div className="font-semibold text-slate-900 text-xs sm:text-sm truncate max-w-[80px] sm:max-w-none">
//                                                                 {o.item}
//                                                             </div>
//                                                             <div className="text-[10px] sm:text-xs text-slate-400 mt-0.5">{o.id}</div>
//                                                         </td>
//                                                         <td className="px-2 sm:px-4 py-3 sm:py-4 text-slate-600 text-xs sm:text-sm hide-mobile">
//                                                             {o.buyer}
//                                                         </td>
//                                                         <td className="px-2 sm:px-4 py-3 sm:py-4 font-medium text-slate-800 text-xs sm:text-sm">
//                                                             {currency(o.amount)}
//                                                         </td>
//                                                         <td className="px-2 sm:px-4 py-3 sm:py-4">
//                                                             <span
//                                                                 className="inline-flex items-center gap-1 px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-[9px] sm:text-xs font-medium whitespace-nowrap"
//                                                                 style={{ backgroundColor: cfg.bg, color: cfg.text }}
//                                                             >
//                                                                 <span
//                                                                     className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full"
//                                                                     style={{ backgroundColor: cfg.dot }}
//                                                                 />
//                                                                 {cfg.label}
//                                                             </span>
//                                                         </td>
//                                                         <td className="px-3 sm:px-6 py-3 sm:py-4 text-right text-slate-500 text-xs sm:text-sm hide-mobile">
//                                                             {formatDate(o.date)}
//                                                         </td>
//                                                     </tr>
//                                                 );
//                                             })
//                                         ) : (
//                                             <tr>
//                                                 <td colSpan={5} className="px-4 sm:px-6 py-8 sm:py-10 text-center text-slate-400 text-xs sm:text-sm">
//                                                     {searchQuery ? 'No orders match your search.' : 'No recent orders found.'}
//                                                 </td>
//                                             </tr>
//                                         )}
//                                     </tbody>
//                                 </table>
//                             </div>
//                         </div>

//                         {/* Machines */}
//                         <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
//                             <div className="px-4 py-3 sm:px-6 sm:py-4 border-b border-slate-100 flex items-center justify-between">
//                                 <h2 className="font-bold text-slate-900 text-[14px] sm:text-[15px]">Machines</h2>
//                                 <span className="text-[10px] sm:text-xs text-slate-400">
//                                     {runningMachines} of {machines.length} running
//                                 </span>
//                             </div>
//                             <div className="divide-y divide-slate-50 max-h-[340px] overflow-y-auto custom-scrollbar">
//                                 {machines.length > 0 ? (
//                                     machines.map((m) => {
//                                         const cfg = MACHINE_STATUS[m.status] || MACHINE_STATUS.idle;
//                                         return (
//                                             <div
//                                                 key={m.id}
//                                                 className="px-4 py-3 sm:px-6 sm:py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-4 hover:bg-slate-50/60 transition-colors"
//                                             >
//                                                 <div className="flex items-center gap-3 min-w-0">
//                                                     <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0">
//                                                         {m.status === "maintenance" || m.status === "error" ? (
//                                                             <Wrench size={14} className="sm:w-4 sm:h-4 text-rose-500" />
//                                                         ) : m.status === "running" ? (
//                                                             <Activity size={14} className="sm:w-4 sm:h-4 text-emerald-500" />
//                                                         ) : (
//                                                             <Activity size={14} className="sm:w-4 sm:h-4 text-slate-400" />
//                                                         )}
//                                                     </div>
//                                                     <div className="min-w-0">
//                                                         <div className="font-medium text-slate-800 text-xs sm:text-sm truncate">
//                                                             {m.name}
//                                                         </div>
//                                                         <div className="flex items-center gap-1.5 text-[10px] sm:text-xs text-slate-400 mt-0.5">
//                                                             <Circle size={6} className="sm:w-[7px] sm:h-[7px]" fill={cfg.color} stroke="none" />
//                                                             {cfg.label} &middot; {m.id}
//                                                         </div>
//                                                     </div>
//                                                 </div>
//                                                 <div className="w-full sm:w-28 flex-shrink-0">
//                                                     <div className="flex items-center justify-between text-[10px] sm:text-xs text-slate-500 mb-1">
//                                                         <span>Utilization</span>
//                                                         <span className="font-semibold text-slate-700">{m.utilization}%</span>
//                                                     </div>
//                                                     <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
//                                                         <div
//                                                             className="h-full rounded-full transition-all duration-500"
//                                                             style={{
//                                                                 width: `${Math.min(Math.max(m.utilization, 0), 100)}%`,
//                                                                 backgroundColor:
//                                                                     m.utilization > 70
//                                                                         ? "#22C55E"
//                                                                         : m.utilization > 30
//                                                                             ? "#4f46e5"
//                                                                             : "#CBD5E1",
//                                                             }}
//                                                         />
//                                                     </div>
//                                                 </div>
//                                             </div>
//                                         );
//                                     })
//                                 ) : (
//                                     <div className="px-4 sm:px-6 py-6 sm:py-8 text-center text-slate-400 text-xs sm:text-sm">
//                                         No machines available.
//                                     </div>
//                                 )}
//                             </div>
//                         </div>
//                     </div>

//                     {/* Right: Chats + This Month */}
//                     <div className="space-y-4 sm:space-y-6">
//                         {/* Chats */}
//                         <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
//                             <div className="px-4 py-3 sm:px-6 sm:py-4 border-b border-slate-100 flex items-center justify-between">
//                                 <h2 className="font-bold text-slate-900 text-[14px] sm:text-[15px]">Chats</h2>
//                                 {unreadChats > 0 && (
//                                     <span className="inline-flex items-center justify-center min-w-[18px] sm:min-w-[20px] h-4 sm:h-5 px-1 rounded-full text-[9px] sm:text-[11px] font-bold bg-indigo-100 text-indigo-700">
//                                         {unreadChats} new
//                                     </span>
//                                 )}
//                             </div>
//                             <div className="divide-y divide-slate-50 max-h-[460px] overflow-y-auto custom-scrollbar">
//                                 {chats.length > 0 ? (
//                                     chats.map((c) => (
//                                         <button
//                                             key={c.id}
//                                             className="w-full text-left px-4 py-3 sm:px-6 sm:py-4 flex items-start gap-2 sm:gap-3 hover:bg-slate-50/60 transition-colors"
//                                         >
//                                             <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-semibold text-[9px] sm:text-xs flex-shrink-0">
//                                                 {c.name
//                                                     .split(" ")
//                                                     .slice(0, 2)
//                                                     .map((w) => w[0])
//                                                     .join("")
//                                                     .toUpperCase()
//                                                     .slice(0, 2)}
//                                             </div>
//                                             <div className="min-w-0 flex-1">
//                                                 <div className="flex items-center justify-between gap-2">
//                                                     <span className="font-medium text-slate-800 text-xs sm:text-sm truncate">
//                                                         {c.name}
//                                                     </span>
//                                                     <span className="text-[9px] sm:text-[11px] text-slate-400 flex-shrink-0">{c.time}</span>
//                                                 </div>
//                                                 <p className="text-[10px] sm:text-xs text-slate-500 truncate mt-0.5">{c.lastMessage}</p>
//                                             </div>
//                                             {c.unread > 0 && (
//                                                 <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-indigo-600 flex-shrink-0 mt-1.5" />
//                                             )}
//                                         </button>
//                                     ))
//                                 ) : (
//                                     <div className="px-4 sm:px-6 py-6 sm:py-8 text-center text-slate-400 text-xs sm:text-sm">
//                                         No chats available.
//                                     </div>
//                                 )}
//                             </div>
//                             <div className="px-4 py-2.5 sm:px-6 sm:py-3 border-t border-slate-100">
//                                 <button className="w-full text-center text-xs sm:text-sm font-medium text-indigo-600 hover:text-indigo-800 flex items-center justify-center gap-1 transition-colors">
//                                     Open all chats <ChevronRight size={12} className="sm:w-[14px] sm:h-[14px]" />
//                                 </button>
//                             </div>
//                         </div>

//                         {/* Quick summary card - This Month */}
//                         <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 space-y-3 sm:space-y-4">
//                             <div className="flex items-center justify-between">
//                                 <h2 className="font-bold text-slate-900 text-[14px] sm:text-[15px]">This Month</h2>
//                                 <span className="text-[10px] sm:text-xs text-slate-400">Updated: {new Date().toLocaleDateString()}</span>
//                             </div>
                            
//                             <div className="space-y-2 sm:space-y-3">
//                                 <div className="flex items-center justify-between text-xs sm:text-sm">
//                                     <span className="text-slate-500 flex items-center gap-1.5">
//                                         <TrendingUp size={12} className="sm:w-[14px] sm:h-[14px]" /> Revenue
//                                     </span>
//                                     <span className="font-semibold text-slate-900">
//                                         {currency(allVendorStats?.revenue || stats.revenue || 0)}
//                                     </span>
//                                 </div>
//                                 <div className="flex items-center justify-between text-xs sm:text-sm">
//                                     <span className="text-slate-500 flex items-center gap-1.5">
//                                         <FileText size={12} className="sm:w-[14px] sm:h-[14px]" /> Quotations sent
//                                     </span>
//                                     <span className="font-semibold text-slate-900">
//                                         {allVendorStats?.quotationsSent || stats.quotationsSent || 0}
//                                     </span>
//                                 </div>
//                                 <div className="flex items-center justify-between text-xs sm:text-sm">
//                                     <span className="text-slate-500 flex items-center gap-1.5">
//                                         <CheckCircle2 size={12} className="sm:w-[14px] sm:h-[14px]" /> Win rate
//                                     </span>
//                                     <span className="font-semibold text-slate-900">41%</span>
//                                 </div>
//                                 <div className="flex items-center justify-between text-xs sm:text-sm">
//                                     <span className="text-slate-500 flex items-center gap-1.5">
//                                         <Clock size={12} className="sm:w-[14px] sm:h-[14px]" /> Avg. response time
//                                     </span>
//                                     <span className="font-semibold text-slate-900">
//                                         {allVendorStats?.avgResponseTime || stats.avgResponseTime || 0} hrs
//                                     </span>
//                                 </div>
//                                 <div className="flex items-center justify-between text-xs sm:text-sm pt-2 border-t border-slate-100">
//                                     <span className="text-slate-500 flex items-center gap-1.5">
//                                         <Package size={12} className="sm:w-[14px] sm:h-[14px]" /> Active Orders
//                                     </span>
//                                     <span className="font-semibold text-slate-900">
//                                         {allVendorStats?.activeOrders || stats.activeOrders || 0}
//                                     </span>
//                                 </div>
//                             </div>
//                         </div>
//                     </div>
//                 </div>
//             </div>
//         </div>
//     );
// }